/*
 * GoogleBusinessProfileCheck
 * A self-check for a law firm's Google Business Profile, built only from the steps in this post.
 * The reader ticks what is already true. No scores, benchmarks or invented numbers.
 *
 * Rules
 *   Items are in three groups, in priority order: Access, Profile details, Reviews.
 *   The result lists every unticked item under its group, in that order, with a one-line fix.
 *   If any Access item is unticked, the result leads with a warning to fix access first,
 *   because a profile nobody at the firm controls can't be edited at all.
 *   If every item is ticked, the result says the basics are covered and links to a free audit.
 *   The result appears after the first tick or untick.
 * No tracking, storage or network calls.
 */
import { useState } from "react";

type Item = { id: string; label: string; fix: string };
type Group = { name: string; items: Item[] };

const GROUPS: Group[] = [
  {
    name: "Access",
    items: [
      { id: "primary", label: "The primary owner is a firm-controlled email", fix: "Move primary ownership to a firm email, not a personal Gmail." },
      { id: "roles", label: "Everyone has their own login through Google's user roles", fix: "Invite each person by role and stop sharing the password." },
      { id: "agency", label: "Any agency has manager access only", fix: "Set your agency to manager so the listing stays yours." },
    ],
  },
  {
    name: "Profile details",
    items: [
      { id: "name", label: "The business name has no added keywords", fix: "Use your real-world firm name only." },
      { id: "category", label: "The most specific primary category is set", fix: "Pick the closest primary category, plus relevant secondary ones." },
      { id: "nap", label: "Name, address and phone match the website exactly", fix: "Make the details identical on the profile and the website." },
      { id: "services", label: "Practice areas are listed in plain language", fix: "List each practice area as a service a client would understand." },
      { id: "photos", label: "Real office and team photos are uploaded", fix: "Add real photos of your office and team." },
      { id: "posts", label: "Updates are posted on a steady schedule", fix: "Post short updates on a regular schedule." },
      { id: "link", label: "The website link goes to the right location or practice page", fix: "Point the link at the matching location or practice page." },
    ],
  },
  {
    name: "Reviews",
    items: [
      { id: "reviewlink", label: "Clients get the profile's direct review link", fix: "Send clients your direct review link so leaving a review is one tap." },
      { id: "noincentive", label: "No incentives, and every client is asked, not just happy ones", fix: "Stop any incentives or screening before asking for reviews." },
      { id: "replies", label: "Replies never confirm that someone was a client", fix: "Use a calm, generic reply that doesn't confirm a client relationship." },
    ],
  },
];

export default function GoogleBusinessProfileCheck() {
  const [have, setHave] = useState<Record<string, boolean>>({});
  const [touched, setTouched] = useState(false);

  const toggle = (id: string) => {
    setHave((s) => ({ ...s, [id]: !s[id] }));
    setTouched(true);
  };

  const gaps = GROUPS.map((g) => ({ name: g.name, missing: g.items.filter((i) => !have[i.id]) })).filter((x) => x.missing.length > 0);
  const accessGap = gaps.some((g) => g.name === "Access");

  const focusRing = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

  return (
    <section className="not-prose my-8 rounded-xl border border-border bg-card p-5 text-card-foreground sm:p-6">
      <h3 className="text-lg font-semibold">Check your firm's Google Business Profile</h3>
      <p className="mt-1 text-sm text-muted-foreground">Tick everything that's already true. Nothing is saved or sent anywhere.</p>

      <div className="mt-5 grid gap-5">
        {GROUPS.map((g) => (
          <fieldset key={g.name}>
            <legend className="text-sm font-semibold">{g.name}</legend>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              {g.items.map((i) => {
                const on = !!have[i.id];
                return (
                  <button
                    key={i.id}
                    type="button"
                    aria-pressed={on}
                    onClick={() => toggle(i.id)}
                    className={`flex items-start gap-3 rounded-md border px-3 py-2 text-left text-sm ${focusRing} ${
                      on ? "border-primary bg-primary text-primary-foreground" : "border-input bg-card"
                    }`}
                  >
                    <span aria-hidden="true" className="mt-0.5 inline-block w-4 shrink-0 font-semibold">
                      {on ? "✓" : "○"}
                    </span>
                    <span>{i.label}</span>
                  </button>
                );
              })}
            </div>
          </fieldset>
        ))}
      </div>

      <div aria-live="polite" className="mt-5 rounded-lg bg-muted p-4 text-sm">
        {!touched ? (
          <p className="text-muted-foreground">Tick what's in place to see what to fix.</p>
        ) : gaps.length === 0 ? (
          <div>
            <p className="font-semibold">The basics are covered.</p>
            <p className="mt-2">Next, keep reviews steady and your citations consistent to build prominence.</p>
            <p className="mt-3">
              <a href="/law-firm-seo-audit" className={`font-medium underline underline-offset-4 ${focusRing}`}>
                Get a free law firm SEO audit →
              </a>
            </p>
          </div>
        ) : (
          <div>
            {accessGap && (
              <p className="font-semibold">
                Fix access first. If the person who holds the account leaves, nobody at the firm can edit the profile.
              </p>
            )}
            {gaps.map((g) => (
              <div key={g.name} className="mt-3">
                <p className="font-semibold">{g.name}</p>
                <ul className="mt-1 list-disc pl-5">
                  {g.missing.map((i) => (
                    <li key={i.id}>{i.fix}</li>
                  ))}
                </ul>
              </div>
            ))}
            <p className="mt-3">
              <a href="/services/local-seo-for-law-firms" className={`font-medium underline underline-offset-4 ${focusRing}`}>
                See our local SEO for law firms service →
              </a>
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
